// Client entry point for the category taxonomy.
//
// The data itself lives in server/config/taxonomy.js and is imported here
// rather than copied: client/package.json declares `"backend": "file:.."`, so
// npm symlinks node_modules/backend to the server directory and the canonical
// file resolves like any other package. That keeps one source of truth for
// ~190 subcategories instead of two files that quietly drift.
//
// Every component imports from THIS file, never from 'backend/...' directly.
// If that resolution ever breaks — a deploy where the symlink isn't recreated,
// a bundler that won't follow it — the fix is to inline the data here, and
// only this file changes.
import {
  FAMILIES,
  CATEGORIES,
  CATEGORY_NAMES,
  slugify,
  normalizeCategory,
  normalizeSubcategory,
  isValidPair,
  familyOf,
} from 'backend/config/taxonomy';

export {
  FAMILIES,
  CATEGORIES,
  CATEGORY_NAMES,
  slugify,
  normalizeCategory,
  normalizeSubcategory,
  isValidPair,
  familyOf,
};

// Subcategories for one category, or [] when the category is unknown.
export function subcategoriesOf(category) {
  const cat = normalizeCategory(category);
  return cat ? CATEGORIES[cat] : [];
}

// Categories in family order, for surfaces that want the grouped list flat.
export const CATEGORIES_IN_FAMILY_ORDER = FAMILIES.flatMap((f) => f.categories);

// Families sorted alphabetically by label, with each family's own categories
// alphabetized too — so no family/category "wins" by sitting first in the
// taxonomy's curated (non-alphabetical) order. Shared by HireTalent's filter
// rail and its browsing rows, so both group categories the same way.
export const FAMILIES_ALPHA = FAMILIES
  .map((fam) => ({
    ...fam,
    categories: [...fam.categories].sort((a, b) => a.localeCompare(b)),
  }))
  .sort((a, b) => a.label.localeCompare(b.label));

// Flat category order matching FAMILIES_ALPHA's grouping — same categories
// as CATEGORIES_IN_FAMILY_ORDER, but families and the categories within each
// are alphabetized rather than in the taxonomy's curated order.
export const CATEGORIES_FAMILY_ALPHA_ORDER = FAMILIES_ALPHA.flatMap((f) => f.categories);

// Ready-made options for the SelectMenu the four creation/edit forms share.
// Family order rather than alphabetical, so "Data" sits next to the other
// technical categories instead of between "Business" and "Digital Marketing".
export const CATEGORY_OPTIONS = CATEGORIES_IN_FAMILY_ORDER.map((c) => ({ value: c, label: c }));

// Options for the subcategory SelectMenu, given the chosen category.
export function subcategoryOptions(category) {
  return subcategoriesOf(category).map((s) => ({ value: s, label: s }));
}
